import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonColor, ButtonModifiers, CustomButton } from 'Douleutaras/components/CustomButton/CustomButton';

import './JobFormExitIntentView.scss';
import SupportHelpBanner from './SupportHelpBanner';

export interface JobFormExitIntentViewProps {
  /** Within working hours. Drives help block's presence, nothing else. */
  supportAvailable: boolean;
  onContinue(): void;
  onCancel(): void;
  onRequestHelp(): void;
}

/**
 * The retention panel shown when a customer tries to abandon the job form (#5622).
 *
 * It replaces the form's body inside the existing modal — the side info, progress
 * bar, back control, and × all stay. The form itself is still mounted behind this,
 * which is what makes "Continue" restore it with everything already filled in.
 *
 * The panel renders identically whether support is available; only the help
 * block at the bottom appears or does not.
 *
 * It takes focus on mount: the form it replaces is `hidden`, so whatever the customer
 * was on has just left the accessibility tree and focus would otherwise fall to the
 * body with nothing announced. `PostJobModal` hands it back on «Continue».
 */
export const JobFormExitIntentView = ({
  supportAvailable,
  onContinue,
  onCancel,
  onRequestHelp
}: JobFormExitIntentViewProps) => {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLElement>(undefined);

  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  return (
    <section styleName="job-form-exit-intent"
             ref={panelRef}
             tabIndex={-1}
             aria-labelledby="job-form-exit-intent-title"
    >
      <header styleName="job-form-exit-intent__header">
        <h2 styleName="job-form-exit-intent__title" id="job-form-exit-intent-title">
          {t('T.POST-JOB.EXIT-INTENT.TITLE', 'Need help?')}
        </h2>
        <p styleName="job-form-exit-intent__subtitle">
          {t('T.POST-JOB.EXIT-INTENT.SUBTITLE', 'Our team is here for whatever you need.')}
        </p>
      </header>

      <div styleName="job-form-exit-intent__actions">
        <CustomButton title={t('T.POST-JOB.EXIT-INTENT.CONTINUE', 'Continue with the job')}
                      modifiers={ButtonModifiers.ROUND}
                      onClick={onContinue}
        />
        <CustomButton title={t('T.POST-JOB.EXIT-INTENT.CANCEL', 'Cancel the request')}
                      modifiers={ButtonModifiers.ROUND}
                      color={ButtonColor.BORDER_BLUE}
                      onClick={onCancel}
        />
      </div>

      { supportAvailable &&
        <div styleName="job-form-exit-intent__support">
          <SupportHelpBanner onRequestHelp={onRequestHelp} />
        </div>
      }
    </section>
  );
};

export default JobFormExitIntentView;
